export const SITE_URL = `https://chictravel.ai`
export const SITE_NAME = `ChicTravel`
export const SITE_DESCRIPTION = `ChicTravel is a collaborative trip planner — itineraries built together on a live map, shared as travel guides, with Chic, your AI copilot, drafting alongside you.`
export const THEME_COLOR = `#FF7F66`
// Purpose-built 1200×630 banner — the size Facebook/LinkedIn/X expect for
// `summary_large_image`. A square logo gets cropped/letterboxed in unfurls.
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`
export const OG_IMAGE_WIDTH = `1200`
export const OG_IMAGE_HEIGHT = `630`

export function absoluteUrl(path: string) {
  return `${SITE_URL}${path.startsWith(`/`) ? path : `/${path}`}`
}

export function createTitle(title?: string) {
  return title
    ? `${title} — ${SITE_NAME}`
    : `${SITE_NAME} — Collaborative AI Trip Planner`
}

export function defaultMeta({
  title,
  description = SITE_DESCRIPTION,
  path = `/`,
  image,
  robots,
  ogType = `website`,
  extra,
}: {
  title?: string
  description?: string
  path?: string
  image?: string
  robots?: string
  ogType?: string
  extra?: Array<Record<string, string>>
} = {}) {
  const fullTitle = createTitle(title)
  const url = absoluteUrl(path)
  const ogImage = image ?? DEFAULT_OG_IMAGE
  // We only know the dimensions of our own default banner; custom guide/blog
  // images have unknown sizes, so we omit width/height rather than lie about them.
  const isDefaultImage = ogImage === DEFAULT_OG_IMAGE

  return {
    meta: [
      { title: fullTitle },
      { name: `description`, content: description },
      { property: `og:title`, content: fullTitle },
      { property: `og:description`, content: description },
      { property: `og:type`, content: ogType },
      { property: `og:url`, content: url },
      { property: `og:site_name`, content: SITE_NAME },
      { property: `og:image`, content: ogImage },
      { property: `og:image:alt`, content: fullTitle },
      ...(isDefaultImage
        ? [
            { property: `og:image:width`, content: OG_IMAGE_WIDTH },
            { property: `og:image:height`, content: OG_IMAGE_HEIGHT },
          ]
        : []),
      { name: `twitter:image`, content: ogImage },
      { name: `twitter:card`, content: `summary_large_image` },
      { name: `twitter:title`, content: fullTitle },
      { name: `twitter:description`, content: description },
      ...(robots ? [{ name: `robots`, content: robots }] : []),
      ...(extra ?? []),
    ],
    links: [{ rel: `canonical`, href: url }],
  }
}

export const noIndex = `noindex, nofollow`
